import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, InputNumber, Select, message } from 'antd';
import { TmsVehicleTypeDetail } from '../../../../graphql/types/TmsVehicleType';
import ImageUploader from 'components/ImageUploader';
import { FuelTypeEnum, getFuelTypeLabel } from '../../../../enums/FuelTypeEnum';
import { useMutation } from '@apollo/client';
import { CREATE_VEHICLE, UPDATE_VEHICLE } from '../graphql/mutations/vehicleMutations';
import EditableDrawer from 'components/EditableDrawer';
import { useTranslation } from 'react-i18next';
import { useVehicleTypes } from '../../../../hooks/useVehicleTypeOptions';
interface TmsVehicleFormDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: TmsVehicleTypeDetail) => void;
  initialValues?: Partial<TmsVehicleTypeDetail>;
  mode: 'create' | 'edit';
  carrierOptions: { label: string; value: number; status?: number }[];
  modelOptions: { label: string; value: number; status: number }[];
  statusOptions: { label: string; value: number }[];
}

const inputStyle = { width: 240 };

const VehicleFormDrawer: React.FC<TmsVehicleFormDrawerProps> = ({
  visible,
  onClose,
  onSubmit,
  initialValues,
  mode,
  carrierOptions,
  modelOptions,
  statusOptions,
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  const { vehicleTypes } = useVehicleTypes();

  const filteredCarrierOptions = useMemo(() => {
    const currentCarrierId = initialValues?.carrierId ? Number(initialValues.carrierId) : undefined;
    return carrierOptions.filter(option => {
      if (option.status === 0) {
        return currentCarrierId !== undefined && option.value === currentCarrierId;
      }
      return true;
    });
  }, [carrierOptions, initialValues?.carrierId]);

  const filteredModelOptions = useMemo(() => {
    return modelOptions.filter(option => option.status === 1);
  }, [modelOptions]);

  const [createVehicle] = useMutation(CREATE_VEHICLE);
  const [updateVehicle] = useMutation(UPDATE_VEHICLE);

  // FuelType下拉
  const fuelTypeOptions = Object.values(FuelTypeEnum)
      .filter(v => typeof v === 'number')
      .map(v => ({ label: getFuelTypeLabel(v as FuelTypeEnum, t), value: v as number }));

  // 处理车型选中后的自动填充
  const handleTypeChange = (vehicleTypeId: number) => {
    const matched = vehicleTypes?.find(vt => String(vt.id) === String(vehicleTypeId));
    if (matched) {
      form.setFieldsValue({
        maxWeightLbs: matched.defaultMaxWeightLbs,
        maxVolumeFt3: matched.defaultMaxVolumeFt3,
        lengthFt: matched.defaultLengthFt,
        widthFt: matched.defaultWidthFt,
        heightFt: matched.defaultHeightFt,
      });
    }
  };

  // 打开抽屉时重置表单并设置初始值
  useEffect(() => {
    if (visible) {
      form.resetFields();

      if (initialValues) {
        const typeOption = modelOptions.find(opt => String(opt.value) === String(initialValues.vehicleTypeId));
        const isTypeSuspended = typeOption?.status === 0;
        form.setFieldsValue({
          ...initialValues,
          vehicleTypeId: (initialValues.vehicleTypeId && !isTypeSuspended) ? String(initialValues.vehicleTypeId) : undefined,
          carrierId: initialValues.carrierId ? Number(initialValues.carrierId) : undefined
        });
      }
    }
  }, [visible, initialValues, form, modelOptions]);

  // 提交表单
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();

      if (mode === 'create') {
        await createVehicle({ variables: { input: values } });
        message.success(t('tms.vehicle.messages.createSuccess') as string);
      } else if (mode === 'edit' && initialValues?.id) {
        values.id = initialValues.id;
        await updateVehicle({ variables: { input: values } });
        message.success(t('tms.vehicle.messages.updateSuccess') as string);
      }

      // 调用父组件的onSubmit函数，触发列表重新查询
      onSubmit(values);
      onClose();
    } catch (error: any) {
      if (error.errorFields) {
        console.error('Form validation failed:', error);
      } else {
        console.error('Submit failed:', error);
        const gqlErr = error?.graphQLErrors?.[0];
        const messageKey: string | undefined = gqlErr?.extensions?.messageKey;
        message.error(messageKey ? t(messageKey) as string : error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const drawerTitle = mode === 'create' ? t('tms.vehicle.pageDetails.new') : t('tms.vehicle.pageDetails.update');

  return (
    <EditableDrawer
      title={drawerTitle}
      visible={visible}
      onClose={onClose}
      onSubmit={handleSubmit}
      confirmText={isSubmitting ? t('tms.vehicle.pageDetails.processing') as string : t('tms.vehicle.pageDetails.confirm') as string}
      cancelText={t('tms.vehicle.pageDetails.cancel') as string}
      confirmDisabled={isSubmitting}
      width={550}
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        initialValues={initialValues}
      >

        <Form.Item name="vehicleNumber" label={t('tms.vehicle.pageDetails.id') as string} hidden={mode !== 'edit'}>
          <Input disabled style={inputStyle} />
        </Form.Item>
        <Form.Item
            name="licensePlate"
            label={t('tms.vehicle.pageDetails.licensePlate') as string}
            rules={[{ required: true, message: t('tms.vehicle.messages.pleaseInputLicensePlate') as string }]}
        >
            <Input placeholder={t('tms.vehicle.placeholders.inputLicensePlate') as string} style={inputStyle} />
        </Form.Item>
        <Form.Item name="carrierId" label={t('tms.vehicle.pageDetails.carrier') as string} rules={[{ required: true, message: t('tms.vehicle.messages.pleaseSelectCarrier') as string }]}>
          <Select
              options={filteredCarrierOptions}
              placeholder={t('tms.vehicle.placeholders.selectCarrier') as string}
              style={inputStyle}
          />
        </Form.Item>
        <Form.Item name="fuelType" label={t('tms.vehicle.pageDetails.fuelType') as string} rules={[{ required: true, message: t('tms.vehicle.messages.pleaseSelectFuelType') as string }]}>
            <Select options={fuelTypeOptions} placeholder={t('tms.vehicle.placeholders.selectFuelType') as string} style={inputStyle} />
        </Form.Item>
        <Form.Item name="vehicleTypeId" label={t('tms.vehicle.pageDetails.model') as string} rules={[{ required: true, message: t('tms.vehicle.messages.pleaseSelectModel') as string }]}>
            <Select
                options={filteredModelOptions}
                placeholder={t('tms.vehicle.placeholders.selectModel') as string}
                style={inputStyle}
                onChange={handleTypeChange}
            />
        </Form.Item>
        <Form.Item name="maxWeightLbs" label={t('tms.vehicle.pageDetails.maxWeightLbs') as string} rules={[{ required: true, type: "number", message: t('tms.vehicle.messages.pleaseInputPayload') as string }]}>
            <InputNumber min={0.01} max={99999999.99} precision={2} style={inputStyle} placeholder={t('tms.vehicle.placeholders.inputPayload') as string} />
        </Form.Item>
        <Form.Item name="maxVolumeFt3" label={t('tms.vehicle.pageDetails.maxVolumeFt3') as string} rules={[{ required: true, type: "number", message: t('tms.vehicle.messages.pleaseInputCapacity') as string }]}>
            <InputNumber min={0.01} max={9999999.999} precision={3} style={inputStyle} placeholder={t('tms.vehicle.placeholders.inputCapacity') as string} />
        </Form.Item>
        <Form.Item name="lengthFt" label={t('tms.vehicle.pageDetails.lengthFt') as string} rules={[{ required: true, type: "number", message: t('tms.vehicle.messages.pleaseInputLength') as string }]}>
            <InputNumber min={0.01} max={999999.99} precision={2} style={inputStyle} placeholder={t('tms.vehicle.placeholders.inputLength') as string} />
        </Form.Item>
        <Form.Item name="widthFt" label={t('tms.vehicle.pageDetails.widthFt') as string} rules={[{ required: true, type: "number", message: t('tms.vehicle.messages.pleaseInputWidth') as string }]}>
            <InputNumber min={0.01} max={999999.99} precision={2} style={inputStyle} placeholder={t('tms.vehicle.placeholders.inputWidth') as string} />
        </Form.Item>
        <Form.Item name="heightFt" label={t('tms.vehicle.pageDetails.heightFt') as string} rules={[{ required: true, type: "number", message: t('tms.vehicle.messages.pleaseInputHeight') as string }]}>
            <InputNumber min={0.01} max={999999.99} precision={2} style={inputStyle} placeholder={t('tms.vehicle.placeholders.inputHeight') as string} />
        </Form.Item>
        <Form.Item name="status" label={t('tms.vehicle.pageDetails.status') as string}>
            <Select options={statusOptions} placeholder={t('tms.vehicle.placeholders.select') as string} style={inputStyle} defaultValue={1} />
        </Form.Item>
        <Form.Item
          name="vehiclePhotos"
          label={t('tms.vehicle.pageDetails.vehiclePhoto') as string}
          rules={[
            { required: true, message: t('tms.vehicle.messages.pleaseUploadVehiclePhotos') as string }
          ]}
        >
          <ImageUploader
              value={initialValues?.vehiclePhotos || []}
              displayUrls={initialValues?.vehiclePhotoUrls || []}
              onChange={(urls: string[]) => form.setFieldsValue({vehiclePhotos: urls})}
              maxCount={3}
          />
        </Form.Item>
      </Form>
    </EditableDrawer>
  );
};

export default VehicleFormDrawer;
